/** Operating system identifier. `web` covers any browser; `ios`/`android` are RN. */
export type PlatformOS = 'ios' | 'android' | 'web';

/**
 * Snapshot of platform state. Stable on native (apart from iPad rotation
 * flipping `isTablet`/`isPhone`); on web, reflects current viewport width
 * and pointer media query.
 */
export interface PlatformInfo {
  /** Underlying operating system. */
  os: PlatformOS;
  /** Apple iPad specifically (iOS + isPad). */
  isPad: boolean;
  /** Android tablet specifically (Android + viewport ≥ tablet breakpoint). */
  isAndroidTablet: boolean;
  /** Android phone (Android + viewport < tablet breakpoint). */
  isAndroidPhone: boolean;
  /** Any tablet form factor (iPad, Android tablet, or web with tablet viewport). */
  isTablet: boolean;
  /** Any phone form factor. */
  isPhone: boolean;
  /** Web with desktop viewport (width >= 1024). */
  isDesktop: boolean;
  /** Touch-capable device. */
  isTouch: boolean;
  /** Native (iOS or Android), not web. */
  isNative: boolean;
}

/**
 * Slot map for `Platform.select` / `usePlatformValue` / `<PlatformGate>`.
 *
 * Precedence (first match wins, most-specific → least-specific):
 *   1. Device:       ipad, iphone, androidTablet, androidPhone
 *   2. Form factor:  tablet, phone, desktop
 *   3. OS:           ios, android, web
 *   4. Native:       native
 *   5. Fallback:     default
 */
export interface PlatformSelectSpec<T> {
  ipad?: T;
  iphone?: T;
  androidTablet?: T;
  androidPhone?: T;
  tablet?: T;
  phone?: T;
  desktop?: T;
  ios?: T;
  android?: T;
  web?: T;
  native?: T;
  default?: T;
}

/** Tablet form factor threshold (px). Below = phone, at/above = tablet. */
export const TABLET_MIN_WIDTH = 768;
/** Desktop form factor threshold (px). At/above = desktop. */
export const DESKTOP_MIN_WIDTH = 1024;
